/**
 * Daily Dungeon: the state a run is made of. Everything here is serialisable JSON; the server
 * persists it whole and the client never sees it (it gets a `DungeonView`).
 */
import type { Pos } from '../grid'

export const MAP_W = 24
export const MAP_H = 24
export const MAP_CELLS = MAP_W * MAP_H
export const INVENTORY_SLOTS = 5
export const LAST_FLOOR = 10
export const BOSS_FLOORS: readonly number[] = [5, 10]
export const MAX_LOG = 30
export const MONSTER_CAP = 12

/** `#` wall · `.` floor · `>` stairs down · `X` sealed stairs (boss alive) · `C` chest */
export type DungeonTile = '#' | '.' | '>' | 'X' | 'C'

export type ClassId = 'knight' | 'rogue' | 'mage'
export type RunStatus = 'active' | 'dead' | 'won' | 'abandoned'
export type MonsterId = 'rat' | 'bat' | 'slime' | 'archer' | 'golem' | 'wraith' | 'ratKing' | 'lich'
export type ItemId =
  | 'potion'
  | 'bigPotion'
  | 'knife'
  | 'fireScroll'
  | 'frostScroll'
  | 'teleportScroll'
  | 'revealScroll'
  | 'sword'
  | 'armor'
  | 'amulet'
export type PerkId =
  | 'vitality'
  | 'brawn'
  | 'ironSkin'
  | 'regeneration'
  | 'vampiric'
  | 'keenEyes'
  | 'scavenger'
  | 'secondWind'
  | 'berserk'
  | 'thorns'

export interface Hero {
  pos: Pos
  cls: ClassId
  hp: number
  maxHp: number
  atk: number
  def: number
  level: number
  xp: number
  /** Sight radius. */
  fov: number
  perks: PerkId[]
  inventory: ItemId[]
  /** The Second wind perk is still unspent. */
  secondWind: boolean
}

export interface DungeonMonster {
  id: number
  kind: MonsterId
  pos: Pos
  hp: number
  maxHp: number
  atk: number
  def: number
  /** Energy gained per hero turn; 100 = one action per turn. */
  speed: number
  energy: number
  awake: boolean
  frozen: number
  /** Slime: already split. Bosses: special already used. */
  split: boolean
  /** Behaviour counter (turns adjacent, summon cooldown…). */
  cd: number
  /** Actions taken so far (wraith blinks on every 4th). */
  acts: number
}

export interface FloorItem {
  pos: Pos
  item: ItemId | 'gold'
  amount: number
}

export interface DungeonRun {
  v: 1
  seed: number
  floor: number
  turn: number
  status: RunStatus
  /** PRNG state; every draw goes through `rngStep` and is written back. */
  rng: number
  /** `MAP_CELLS` tile characters, row-major. */
  tiles: string
  /** `MAP_CELLS` of '0' / '1': cells the hero has ever seen on this floor. */
  seen: string
  hero: Hero
  monsters: DungeonMonster[]
  items: FloorItem[]
  nextId: number
  kills: number
  gold: number
  /** Perk choices waiting for the player; nothing else may happen until one is picked. */
  pendingPerks: PerkId[]
  bossAlive: boolean
  /** Permanent items (sword, armor, amulet) still allowed to drop on this floor. */
  permanentLeft: number
  log: string[]
}

export function idx(p: Pos): number {
  return p.r * MAP_W + p.c
}
export function posOf(i: number): Pos {
  return { r: Math.floor(i / MAP_W), c: i % MAP_W }
}
export function inMap(p: Pos): boolean {
  return p.r >= 0 && p.c >= 0 && p.r < MAP_H && p.c < MAP_W
}
export function manhattan(a: Pos, b: Pos): number {
  return Math.abs(a.r - b.r) + Math.abs(a.c - b.c)
}
export function chebyshev(a: Pos, b: Pos): number {
  return Math.max(Math.abs(a.r - b.r), Math.abs(a.c - b.c))
}
export function isWalkable(tile: string): boolean {
  return tile === '.' || tile === '>' || tile === 'C'
}
